<template>
<div class='cit-wrapper'>
   <div class='title'>
      Overall Improvement Scenario
      <span class='magnify' @click="showModal" content="Click to use the cooking improvement tool." v-tippy>
         <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-search" viewBox="0 0 16 16">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            <g transform='translate(-1.5,-1.5)'>
               <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </g>
         </svg>
      </span>
   </div>
   <el-dialog class="ci-table" title="Overall Cooking Improvement Scenario" :visible.sync="showEnlarged">
      <div style="padding: 0 1em 1em 1em">
         <el-table :data="tableData">
            <el-table-column
               v-for="{label, prop, width, sortable, formatter} in tableColumns"
               :key="prop"
               :prop="prop"
               :width="width"
               :sortable="sortable"
               :label="label"
               :formatter="formatter"
            />
         </el-table>
         <input-field
            label="No of households"
            placeholder="No of households"
            type="number"
            :extra-attributes="{step: 1, min: 0}"
            v-model="numOfHouseholds"
            :errors="[]"
            required
            one-line
            label50
         />
         <input-field
            label="Cost per HH(Partial Scenario)"
            type="number"
            :extra-attributes="{step: 1, min: 0}"
            v-model="perHHCostPartial"
            :errors="[]"
            required
            one-line
            label50
         />
         <input-field
            label="Cost per HH(Intermediate Scenario)"
            placeholder="No of households"
            type="number"
            :extra-attributes="{step: 1, min: 0}"
            v-model="perHHCostIntermediate"
            :errors="[]"
            required
            one-line
            label50
         />
         <hr>
         <p class='total'>Total cost for partial scenario:  {{partialScenarioCost}}</p>
         <p class='total'>Total cost for intermediate scenario: {{intermediateScenarioCost}}</p>
      </div>
   </el-dialog>
   <div style="overflow: auto;">
      <el-table :data="tableData">
         <el-table-column
            v-for="{label, prop, width, sortable, formatter} in tableColumns"
            :key="prop"
            :prop="prop"
            :width="width"
            :sortable="sortable"
            :label="label"
            :formatter="formatter"
         />
      </el-table>
   </div>
</div>
</template>

<script>
import InputField from '../../../molecules/Login/InputField';
import { Table, TableColumn, Dialog } from 'element-ui';
import { formatCurrency } from '../../../../js/ui-helpers';
import { mapGetters } from 'vuex';

const tableColumns = [
   { label: "Primary Stove",          width: 140, prop: "primary_stove", sortable: false },
   { label: "Scenario: Baseline",       width: 150, prop: "baseline_lng",      sortable: false },
   { label: "Scenario: Partial",      width: 130, prop: "partial_lng",       sortable: false },
   { label: "Scenario: Intermediate", width: 180, prop: "intermediate_lng",  sortable: false },
];

export default {
   components: {
      'el-table': Table,
      'el-table-column': TableColumn,
      'el-dialog': Dialog,
      InputField,
   },
   data() {
      return {
         tableColumns,
         showEnlarged: false,
         numOfHouseholds: 0,
         perHHCostPartial: 0,
         perHHCostIntermediate: 0,
      }
   },
   computed: {
      ...mapGetters('infographics', ["getCookingImprovementData"]),
      tableData() {
         if (this.getCookingImprovementData)
         {
            const data = [
               {primary_stove: "Advanced"},
               {primary_stove: "Modern"},
               {primary_stove: "Intermediate"},
               {primary_stove: "Partial"},
               {primary_stove: "Low"},
            ];
            const getDataByStove = stove => data.find(item => item.primary_stove===stove);
            const percents = this.getCookingImprovementData.percentages;
            // intialize every state with baseline data
            data.forEach(item =>
            {
               item.baseline = item.partial = item.intermediate = percents[item.primary_stove];
               item.baseline_lng = item.partial_lng = item.intermediate_lng =`${item.baseline.toFixed(1)}% (${parseInt(item.baseline * this.numOfHouseholds/100)})`;
            });
            const lowDataItem = getDataByStove('Low');
            const partialDataItem = getDataByStove('Partial');
            const intermediateDataItem = getDataByStove('Intermediate')            
            // update for partial scenario
            lowDataItem.partial = 0;
            lowDataItem.partial_lng = '0.0%';
            partialDataItem.partial = lowDataItem.baseline + partialDataItem.baseline;
            partialDataItem.partial_lng = `${partialDataItem.partial.toFixed(1)}% (${parseInt(partialDataItem.partial * this.numOfHouseholds / 100)})`;
            // update for intermediate scenario
            lowDataItem.intermediate = 0;
            lowDataItem.intermediate_lng = '0.0%';
            partialDataItem.intermediate = 0;
            partialDataItem.intermediate_lng = '0.0%';
            intermediateDataItem.intermediate = intermediateDataItem.partial + partialDataItem.partial;
            intermediateDataItem.intermediate_lng = `${intermediateDataItem.intermediate.toFixed(1)}% (${parseInt(intermediateDataItem.intermediate* this.numOfHouseholds / 100)})`;
            return data;
         }
         return []
      },
      baselineLowPercentage() {
         const lowData = this.tableData.find(item => item.primary_stove==='Low') 
         return lowData ? lowData.baseline : 0;
      },
      partialScenarioCost() {
         return formatCurrency(this.numOfHouseholds*this.perHHCostPartial * this.baselineLowPercentage/100);
      },
      partialPartialPercentage() {
         const partialData = this.tableData.find(item => item.primary_stove==='Partial') 
         return partialData ? partialData.partial : 0;
      },
      intermediateScenarioCost() {
         return formatCurrency(this.numOfHouseholds*this.perHHCostIntermediate * this.partialPartialPercentage/100);
      },
   },
   methods: {
      showModal()
      {
         this.showEnlarged = true;
      }
   }
}
</script>

<style lang='scss'>
.ci-table > .el-dialog {
   width: fit-content;
}
.cit-wrapper {
   text-align: center;
   .label {
      font-size: 0.95em;
   }
   .total {
      padding: 0.4em 0 ;
   }
   .el-table td:not(:first-child) .cell {
      text-align: center;
   }
   .title {
      text-align: center;
      font-size: 1em;
      font-weight: 600;
      font-family: $primary-font;
      color: lighten($font-color, 10%);
      padding: 0.5em;

   }
   .magnify
   {
      float: left;
      margin-top: -5px;
      margin-left: -5px;
      padding: 5px 8px 0px 8px;
      border: solid 1px lighten($font-color, 10%) ;
      border-radius: 2px;
      &:hover{ 
         cursor: pointer;
      }
   }
}
</style>
